// FB2 §2 (결정 H) — 캘린더 카테고리 공유 모델.
//
// IntegratedCalendar 와 UpcomingEventsPanel 이 동일한 4-카테고리 모델
// (설치/발주/납품/유지보수)·아이콘·색상·표시 순서·localStorage 영속 헬퍼를
// 공유하기 위해 추출한 모듈. 종전에는 IntegratedCalendar 내부에만 있었고
// 패널은 자체 3-카테고리(install/delivery/service)를 따로 들고 있었다.
//
// 카테고리 데이터 소스:
//   🚚 install     ← InfrastructureOrder.installScheduledAt (&& !installCompletedAt)
//   📐 order       ← InfrastructureOrder.grillOrderedAt        (그릴러 발주)
//   📦 delivery    ← InfrastructureOrder.grillDeliveredAt      (그릴러 납품)
//   🛠️ maintenance ← ServiceOrder.scheduledFor (PREVENTIVE/REACTIVE, 유지보수)
//
// load/save 헬퍼는 storageKey 를 인자로 받아 소비처(캘린더/패널)별로 독립
// 영속한다 — 한 화면의 기본값이 다른 화면의 저장값을 덮어쓰지 않도록.

import type { IconName } from './Icon';

export type CalendarCategory =
  | 'install'
  | 'installDone'
  | 'maintenance'
  | 'delivery'
  | 'order'
  | 'return'
  | 'replace'
  | 'renewal'
  | 'event';

// 표시 순서 = 설치 → 설치 완료 → 발주 → 납품 → 유지보수.
// round6 §1-4(b) — 'installDone'(설치 완료, installCompletedAt) 카테고리:
// 완료 처리하는 순간 캘린더에서 일정이 사라져 "반영 안 됨"으로 보이던 문제의
// 해소 — 완료 건도 완료일에 이력으로 남는다.
export const CATEGORY_ORDER: CalendarCategory[] = [
  'install',
  'installDone',
  'order',
  'delivery',
  'maintenance',
  // 2026-10-19 — 회수(Customer.returnScheduledAt). 통합 캘린더에서 회수 일정을 추가할 수 있게.
  'return',
  // 2026-10-20 — 로봇 교체(설치 + 회수를 한 번에). '로봇 교체' 서비스 오더의 방문 예정일.
  'replace',
  // 2026-10-22 — 재계약(옛 고객사 → 새 고객사). 옛 계약 마감 + 새 계약 활성화.
  'renewal',
  // 2026-11-02 — 이벤트(행사장 설치 · 회수). 일정 하나가 칩 두 개([이벤트 설치] · [이벤트 회수]).
  'event',
];

// 규칙1(이모지 금지) — 아이콘은 K_ICONS name(iconName)으로, 색은 Kuun 토큰으로.
// 4 카테고리 = 구분 색상 팔레트(install teal / order purple / delivery amber / maintenance red).
//
// §9(아이콘 정본화) — docs/design/icon-vocabulary.md 기준으로 두 자리를 바꿨다.
//   install: truck → bot     (truck 은 물류=그릴러 발주/인프라 몫으로 넘김)
//   order  : factory → truck (factory 는 제조 전용 — 발주와 겹쳐 있었다)
// 이 파일이 범례 아이콘의 단일 출처다. 소비처는 CATEGORY_META[k].iconName 만 읽는다.
export const CATEGORY_META: Record<
  CalendarCategory,
  {
    iconName: IconName;
    label: string;
    color: string;
    bg: string;
    /** 칸 안의 일정 칩을 꽉 찬 바탕으로(2026-11-02 이벤트 — "구분이 확실한 색"). [바탕, 글씨]. */
    solid?: [string, string];
  }
> = {
  install: {
    iconName: 'bot',
    label: '로봇 설치',
    color: 'var(--fg-brand)',
    bg: 'var(--brand-subtle)',
  },
  // round6 §1-4(b) — 설치 완료(installCompletedAt). 예정과 구분되는 체크 아이콘.
  installDone: {
    iconName: 'check-circle',
    label: '설치 완료',
    // 2026-10-19 오너 — 설치 완료는 파랑(로봇 설치 청록과 갈리게).
    color: 'var(--robot-shipping)',
    bg: 'var(--robot-shipping-tint)',
  },
  // FB §6 — 그릴러 발주(grillOrderedAt). 납품(package)과 구분되는 아이콘(truck) + 보라 톤.
  order: {
    iconName: 'truck',
    label: '그릴러 발주',
    color: 'var(--robot-maintenance)',
    bg: 'var(--robot-maintenance-tint)',
  },
  maintenance: {
    iconName: 'wrench',
    label: '유지보수',
    color: 'var(--fg-danger)',
    bg: 'var(--bg-danger-subtle)',
  },
  delivery: {
    iconName: 'package',
    label: '그릴러 납품',
    color: 'var(--fg-on-warning-tint)',
    bg: 'var(--bg-warning-subtle)',
  },
  return: {
    iconName: 'undo',
    label: '회수',
    // 2026-10-19 오너 — 회수는 눈에 띄는 진분홍(주황 → "좀 눈에 띄는 색으로").
    color: 'var(--cal-return)',
    bg: 'var(--cal-return-tint)',
  },
  replace: {
    iconName: 'swap',
    label: '로봇 교체',
    color: 'var(--cal-replace)',
    bg: 'var(--cal-replace-tint)',
  },
  renewal: {
    iconName: 'signature',
    label: '재계약',
    color: 'var(--cal-renewal)',
    bg: 'var(--cal-renewal-tint)',
  },
  event: {
    iconName: 'sparkles',
    label: '이벤트',
    color: 'var(--cal-event)',
    bg: 'var(--cal-event-tint)',
    solid: ['var(--cal-event-solid)', 'var(--cal-event-solid-fg)'],
  },
};

// 설치(예정/완료)만 기본 ON, 나머지(발주/납품/유지보수)는 기본 OFF.
export const DEFAULT_ACTIVE: Record<CalendarCategory, boolean> = {
  install: true,
  installDone: true,
  order: false,
  maintenance: false,
  delivery: false,
  return: false,
  replace: false,
  renewal: false,
  event: false,
};

export function loadActiveCategories(
  storageKey: string,
  defaults: Record<CalendarCategory, boolean>,
): Record<CalendarCategory, boolean> {
  if (typeof window === 'undefined') return { ...defaults };
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Partial<Record<CalendarCategory, unknown>>;
    // 저장된 값이 boolean 이면 그것을, 아니면(누락된 신규 카테고리 포함) 기본값.
    return {
      install: typeof parsed.install === 'boolean' ? parsed.install : defaults.install,
      installDone:
        typeof parsed.installDone === 'boolean' ? parsed.installDone : defaults.installDone,
      order: typeof parsed.order === 'boolean' ? parsed.order : defaults.order,
      maintenance:
        typeof parsed.maintenance === 'boolean' ? parsed.maintenance : defaults.maintenance,
      delivery: typeof parsed.delivery === 'boolean' ? parsed.delivery : defaults.delivery,
      return: typeof parsed.return === 'boolean' ? parsed.return : defaults.return,
      replace: typeof parsed.replace === 'boolean' ? parsed.replace : defaults.replace,
      renewal: typeof parsed.renewal === 'boolean' ? parsed.renewal : defaults.renewal,
      event: typeof parsed.event === 'boolean' ? parsed.event : defaults.event,
    };
  } catch {
    return { ...defaults };
  }
}

export function saveActiveCategories(
  storageKey: string,
  active: Record<CalendarCategory, boolean>,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(active));
  } catch {
    // localStorage quota / private mode — silently ignore.
  }
}
